import { VideoTranscriptElement } from '@src/editor/models/elements/VideoTranscriptElement';

import { LogManager } from '../../../utility/Logger';
import { ClipTranscriptSegment } from '../../models/ClipTranscriptSegment';
import { splitTokenValueIntoTokens } from '../../services/transcriptTokenUtils';
import { AbstractCommand } from '../AbstractCommand';

import type {
  ClipTranscriptTextToken,
  ClipTranscriptToken,
} from '@src/editor/types';
import type { VideoEditor } from '@src/editor/VideoEditor';

const logger = LogManager.getLogger('transcript:replace');

export class TranscriptTextReplaceCommand extends AbstractCommand {
  private readonly segmentId: string;
  private readonly fromIndex: number;
  private readonly toIndex: number;
  private readonly newText: string;

  private previousSegment: ClipTranscriptSegment | null = null;
  private element: VideoTranscriptElement | null = null;

  constructor(
    editor: VideoEditor,
    segmentId: string,
    fromIndex: number,
    toIndex: number,
    newText: string,
  ) {
    super(editor);
    this.segmentId = segmentId;
    this.fromIndex = Math.min(fromIndex, toIndex);
    this.toIndex = Math.max(fromIndex, toIndex);
    this.newText = newText;
  }

  execute(): boolean {
    const track = this.editor.getVideoTranscriptTrack();
    if (!track) {
      logger.error('No transcript track available');
      return false;
    }

    const el = track.transcriptSegmentElements.find(
      el => el.getClipTranscriptSegment().id === this.segmentId,
    );
    if (!(el instanceof VideoTranscriptElement)) {
      logger.error('Segment element not found or invalid', {
        segmentId: this.segmentId,
      });
      return false;
    }
    this.element = el;

    const oldSegment = el.getClipTranscriptSegment();

    const newSegment = this.createNewSegmentWithWordsReplaced(oldSegment);
    if (!newSegment) return false;

    this.previousSegment = oldSegment;
    el.setClipTranscriptSegment(newSegment);

    // Recalculate timeline duration and reconcile dependent tracks
    this.recalculateTimeline();
    this.editor.markDirty('transcript_text_replace');

    return true;
  }

  undo(): boolean {
    if (!this.element || !this.previousSegment) return false;
    this.element.setClipTranscriptSegment(this.previousSegment);
    this.recalculateTimeline();
    this.editor.markDirty('transcript_text_replace_undo');
    return true;
  }

  getName(): string {
    return 'TranscriptTextReplaceCommand';
  }

  getDescription(): string {
    return `Replace words (${this.fromIndex}-${this.toIndex}) from segment ${this.segmentId} with new text: ${this.newText}`;
  }

  private createNewSegmentWithWordsReplaced(
    segment: ClipTranscriptSegment,
  ): ClipTranscriptSegment | false {
    const tokens = segment.getWordsWithPunctuation();

    if (this.fromIndex < 0 || this.toIndex > tokens.length - 1) {
      logger.error('index range out of bounds', {
        fromIndex: this.fromIndex,
        toIndex: this.toIndex,
      });
      return false;
    }

    // Create new tokens array immutably with correct slice (exclude toIndex)
    // If new text is empty, simply remove the range
    const newTokens: ClipTranscriptToken[] = [
      ...tokens.slice(0, this.fromIndex),
      ...this.splitNewTextValueIntoTokens(segment),
      ...tokens.slice(this.toIndex + 1),
    ];

    // Preserve media time boundaries
    return new ClipTranscriptSegment(
      newTokens,
      segment.mediaStart,
      segment.mediaEnd,
    );
  }

  private splitNewTextValueIntoTokens(
    segment: ClipTranscriptSegment,
  ): ClipTranscriptToken[] {
    const tokens = segment.getWordsWithPunctuation();

    // Determine the replacement interval boundaries (relative timeline space)
    const segmentDuration = segment.mediaEnd - segment.mediaStart;
    const replacedRangeTokens = tokens.slice(this.fromIndex, this.toIndex + 1);
    const isClipTextToken = (
      t: ClipTranscriptToken,
    ): t is ClipTranscriptTextToken =>
      t.type === 'text' && 'relative_ts' in t && 'relative_end_ts' in t;

    const replacedTextTokens = replacedRangeTokens.filter(isClipTextToken);

    let intervalStart: number;
    let intervalEnd: number;

    // Extract context from the first replaced text token to preserve paragraph formatting
    const contextToken = replacedTextTokens[0];
    const context = contextToken
      ? {
          paragraph_id: contextToken.paragraph_id,
          speaker_id: contextToken.speaker_id,
          speaker_label: contextToken.speaker_label,
          segment_id: contextToken.segment_id,
        }
      : undefined;

    if (replacedTextTokens.length > 0) {
      // Use the span of the existing text tokens being replaced
      const first = replacedTextTokens[0];
      const last = replacedTextTokens[replacedTextTokens.length - 1];
      intervalStart = first.relative_ts;
      intervalEnd = last.relative_end_ts;
    } else {
      // No text tokens in the replaced range (punctuation only). Use surrounding boundaries.
      const prev = ClipTranscriptSegment.findPreviousTextWord(
        this.fromIndex,
        tokens,
      );
      const next = ClipTranscriptSegment.findNextTextWord(this.toIndex, tokens);
      intervalStart = prev && isClipTextToken(prev) ? prev.relative_end_ts : 0;
      intervalEnd =
        next && isClipTextToken(next) ? next.relative_ts : segmentDuration;
      if (intervalEnd < intervalStart) {
        // Degenerate case; collapse interval
        intervalEnd = intervalStart;
      }
    }

    const availableDuration = Math.max(0, intervalEnd - intervalStart);
    const mediaStart = segment.mediaStart + intervalStart;

    // Use the shared utility to split the new text value into tokens.
    // Pass context to preserve paragraph_id and other properties, and the
    // replaced text tokens so each replacement word inherits the identity of
    // the word it replaces (PLAT-10266) instead of losing its translation link.
    return splitTokenValueIntoTokens(
      this.newText,
      mediaStart,
      intervalStart,
      availableDuration,
      -1,
      'added',
      context,
      replacedTextTokens,
    );
  }
}
